/**
 * ManagedSqlDbTable Component
 *
 * Displays a table of managed SQL databases with actions (view, edit, delete).
 */

import React from 'react';
import {
  ServerStackIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ManagedSqlDbStatusBadge } from './ManagedSqlDbStatusBadge';
import type { ManagedSqlDbSanitized } from '../../../types/managed-sql-db.types';
import { ManagedSqlDbStatus } from '../../../types/managed-sql-db.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface ManagedSqlDbTableProps {
  managedSqlDbs: ManagedSqlDbSanitized[];
  isLoading?: boolean;
  onView: (db: ManagedSqlDbSanitized) => void;
  onEdit?: (db: ManagedSqlDbSanitized) => void;
  onDelete?: (db: ManagedSqlDbSanitized) => void;
  /** Permission to edit managed SQL databases */
  canEdit?: boolean;
  /** Permission to delete managed SQL databases */
  canDelete?: boolean;
}

/**
 * ManagedSqlDbTable component
 */
export const ManagedSqlDbTable: React.FC<ManagedSqlDbTableProps> = ({
  managedSqlDbs,
  isLoading,
  onView,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (managedSqlDbs.length === 0) {
    return (
      <EmptyState
        icon={<ServerStackIcon className="mx-auto h-12 w-12" />}
        title="No managed SQL databases found"
        description="Get started by creating a new managed SQL database."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Code
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Grants
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {managedSqlDbs.map((item) => {
            const appGrantsCount = item.applicationGrants?.length || 0;
            const eltGrantsCount = item.eltJobGrants?.length || 0;
            const isAvailable = item.status === ManagedSqlDbStatus.AVAILABLE;

            return (
              <tr key={item.managedSqlDbId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <ServerStackIcon className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {item.managedSqlDbName}
                      </div>
                      {item.description && (
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {item.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <CopyableCode value={item.managedSqlDbCode} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ManagedSqlDbStatusBadge
                    status={item.status}
                    statusMessage={item.statusMessage}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {appGrantsCount} app{appGrantsCount !== 1 ? 's' : ''}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      {eltGrantsCount} ELT job{eltGrantsCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatRelativeTime(item.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onView(item)}
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                    {canEdit && onEdit && isAvailable && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onEdit(item)}
                        title="Edit Database"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && onDelete && isAvailable && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDelete(item)}
                        title="Delete Database"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
